import React from 'react';
import { View, Text } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';

interface DealReadinessCardProps {
  present: number;
  total: number;
  missingCritical: Array<{ name: string }>;
  missingRecommended: Array<{ name: string }>;
}

export function DealReadinessCard({
  present,
  total,
  missingCritical,
  missingRecommended,
}: DealReadinessCardProps) {
  const colors = useThemeColors();
  const progressPercent = total > 0 ? (present / total) * 100 : 0;

  return (
    <View
      style={{
        backgroundColor: colors.amber.light,
        borderWidth: 1,
        borderColor: colors.amber.muted,
        borderRadius: 12,
        padding: 14,
        marginBottom: 4,
      }}
    >
      {/* Progress: count + bar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Text
          style={{
            color: colors.text.primary,
            fontSize: 18,
            fontWeight: '700',
            letterSpacing: -0.3,
          }}
          numberOfLines={1}
        >
          {present} <Text style={{ color: colors.text.tertiary, fontSize: 13, fontWeight: '500' }}>of {total}</Text>
        </Text>
        <Text
          style={{
            color: colors.text.tertiary,
            fontSize: 11,
            fontWeight: '500',
          }}
          numberOfLines={1}
        >
          documents
        </Text>
      </View>

      <View
        style={{
          backgroundColor: colors.surface.tertiary,
          borderRadius: 4,
          height: 6,
          overflow: 'hidden',
          marginBottom: missingCritical.length > 0 || missingRecommended.length > 0 ? 14 : 0,
        }}
      >
        <View
          style={{
            width: `${progressPercent}%`,
            height: 6,
            borderRadius: 4,
            backgroundColor: colors.warning,
          }}
        />
      </View>

      {missingCritical.length > 0 && (
        <View style={{ marginBottom: missingRecommended.length > 0 ? 10 : 0 }}>
          <Text
            style={{
              color: colors.text.tertiary,
              fontSize: 9,
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            Missing Critical
          </Text>
          {missingCritical.map((doc, i) => (
            <View
              key={`crit-${i}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <Text
                style={{
                  color: colors.error,
                  fontSize: 13,
                  marginRight: 6,
                  lineHeight: 18,
                  width: 14,
                }}
              >
                ✕
              </Text>
              <Text
                style={{
                  color: colors.text.primary,
                  fontSize: 13,
                  lineHeight: 18,
                  flex: 1,
                }}
                numberOfLines={1}
              >
                {doc.name}
              </Text>
            </View>
          ))}
        </View>
      )}

      {missingRecommended.length > 0 && (
        <View>
          <Text
            style={{
              color: colors.text.tertiary,
              fontSize: 9,
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            Missing Recommended
          </Text>
          {missingRecommended.map((doc, i) => (
            <View
              key={`rec-${i}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 4,
              }}
            >
              <Text
                style={{
                  color: colors.warning,
                  fontSize: 13,
                  marginRight: 6,
                  lineHeight: 18,
                  width: 14,
                }}
              >
                ○
              </Text>
              <Text
                style={{
                  color: colors.text.secondary,
                  fontSize: 13,
                  lineHeight: 18,
                  flex: 1,
                }}
                numberOfLines={1}
              >
                {doc.name}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
